import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { hasPermission } from '../lib/permissions';
import { openProtectedFile } from '../lib/protectedFiles';
import CameraFileInput from './CameraFileInput';
import { 
  Leaf, 
  Skull, 
  Calendar, 
  MapPin, 
  FileText, 
  AlertTriangle,
  CheckCircle,
  Undo2,
  Clock,
  Building2,
  Heart,
  Download,
  Loader2,
  User,
  FileCheck,
  Syringe,
  Home,
  HelpCircle,
  AlertCircle,
  Trash2,
  Pencil
} from 'lucide-react';

// Tipos de desfecho
const OUTCOME_TYPES = [
  { value: 'alta_clinica', label: 'Alta Clínica', icon: Heart, color: 'bg-green-100 text-green-800 border-green-300', description: 'Animal recuperado e liberado para o responsável' },
  { value: 'soltura', label: 'Soltura em Ambiente Natural', icon: Leaf, color: 'bg-emerald-100 text-emerald-800 border-emerald-300', description: 'Liberação do animal na natureza' },
  { value: 'transferencia', label: 'Transferência Definitiva', icon: Building2, color: 'bg-blue-100 text-blue-800 border-blue-300', description: 'Transferência para outro CRAS, zoo ou mantenedor' },
  { value: 'obito', label: 'Óbito', icon: Skull, color: 'bg-gray-100 text-gray-800 border-gray-300', description: 'Óbito natural do animal' },
  { value: 'eutanasia', label: 'Eutanásia', icon: Syringe, color: 'bg-red-100 text-red-800 border-red-300', description: 'Eutanásia humanitária quando necessário' },
  { value: 'permanencia', label: 'Permanência Permanente', icon: Home, color: 'bg-purple-100 text-purple-800 border-purple-300', description: 'Animal irrecuperável permanece no CRAS' },
  { value: 'outro', label: 'Outro', icon: HelpCircle, color: 'bg-yellow-100 text-yellow-800 border-yellow-300', description: 'Outro tipo de desfecho com justificativa' },
];

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 
  'SP', 'SE', 'TO'
];

const getApiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((err) => {
        const location = Array.isArray(err.loc) ? err.loc.join('.') : '';
        return [location, err.msg].filter(Boolean).join(': ');
      })
      .join(', ');
  }
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return fallback;
};

const formatOutcomeDate = (value) => {
  if (!value) return 'N/A';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

const splitOutroOutcomeDetails = (value) => {
  const text = String(value || '');
  const match = text.match(/^Tipo informado:\s*(.*?)\s*\|\s*(.*)$/);
  if (!match) {
    return { tipo: '', justificativa: text };
  }
  return { tipo: match[1] || '', justificativa: match[2] || '' };
};

export default function OutcomeTab({ animalId, animal, user, onOutcomeChange }) {
  const [outcome, setOutcome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentType, setDocumentType] = useState('outro_documento');
  const [documentName, setDocumentName] = useState('');

  const handleOpenAttachment = async (event, attachment) => {
    event.preventDefault();
    try {
      await openProtectedFile(attachment.file_url, attachment.document_name || attachment.filename);
    } catch (error) {
      toast.error(error.message || 'Não foi possível abrir o arquivo');
    }
  };
  
  // Form state
  const [outcomeType, setOutcomeType] = useState('');
  const [outcomeDate, setOutcomeDate] = useState('');
  const [outcomeTime, setOutcomeTime] = useState('');
  const [justificativaClinica, setJustificativaClinica] = useState('');
  const [observacoesAdmin, setObservacoesAdmin] = useState('');
  
  // Alta Clínica
  const [condicaoClinicaFinal, setCondicaoClinicaFinal] = useState('');
  const [recomendacoesPosAlta, setRecomendacoesPosAlta] = useState('');
  const [responsavelRetiradaNome, setResponsavelRetiradaNome] = useState('');
  const [responsavelRetiradaDoc, setResponsavelRetiradaDoc] = useState('');
  
  // Soltura
  const [localSolturaCidade, setLocalSolturaCidade] = useState('');
  const [localSolturaEstado, setLocalSolturaEstado] = useState('');
  const [localSolturaCoordenadas, setLocalSolturaCoordenadas] = useState('');
  const [orgaoAutorizador, setOrgaoAutorizador] = useState('');
  
  // Transferência
  const [instituicaoDestino, setInstituicaoDestino] = useState('');
  const [responsavelDestino, setResponsavelDestino] = useState('');
  const [observacoesClinicasEnviadas, setObservacoesClinicasEnviadas] = useState('');
  
  // Óbito/Eutanásia
  const [causaPresumida, setCausaPresumida] = useState('');
  const [necropsiaRealizada, setNecropsiaRealizada] = useState(false);
  const [observacoesObito, setObservacoesObito] = useState('');
  
  // Outro
  const [outroTipoDesfecho, setOutroTipoDesfecho] = useState('');
  const [justificativaOutro, setJustificativaOutro] = useState('');

  useEffect(() => {
    if (animalId) {
      fetchOutcome();
    }
  }, [animalId]);

  const fetchOutcome = async () => {
    try {
      const response = await axios.get(`${API}/animals/${animalId}/outcome`);
      setOutcome(response.data);
    } catch (error) {
      setOutcome(null);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setOutcomeType('');
    setOutcomeDate('');
    setOutcomeTime('');
    setJustificativaClinica('');
    setObservacoesAdmin('');
    setCondicaoClinicaFinal('');
    setRecomendacoesPosAlta('');
    setResponsavelRetiradaNome('');
    setResponsavelRetiradaDoc('');
    setLocalSolturaCidade('');
    setLocalSolturaEstado('');
    setLocalSolturaCoordenadas('');
    setOrgaoAutorizador('');
    setInstituicaoDestino('');
    setResponsavelDestino('');
    setObservacoesClinicasEnviadas('');
    setCausaPresumida('');
    setNecropsiaRealizada(false);
    setObservacoesObito('');
    setOutroTipoDesfecho('');
    setJustificativaOutro('');
  };

  const populateFormFromOutcome = (currentOutcome) => {
    const outroDetails = splitOutroOutcomeDetails(currentOutcome?.justificativa_outro);
    setOutcomeType(currentOutcome?.outcome_type || '');
    setOutcomeDate(currentOutcome?.outcome_date ? String(currentOutcome.outcome_date).slice(0, 10) : '');
    setOutcomeTime(currentOutcome?.outcome_time || '');
    setJustificativaClinica(currentOutcome?.justificativa_clinica || '');
    setObservacoesAdmin(currentOutcome?.observacoes_administrativas || '');
    setCondicaoClinicaFinal(currentOutcome?.condicao_clinica_final || '');
    setRecomendacoesPosAlta(currentOutcome?.recomendacoes_pos_alta || '');
    setResponsavelRetiradaNome(currentOutcome?.responsavel_retirada_nome || '');
    setResponsavelRetiradaDoc(currentOutcome?.responsavel_retirada_documento || '');
    setLocalSolturaCidade(currentOutcome?.local_soltura_cidade || '');
    setLocalSolturaEstado(currentOutcome?.local_soltura_estado || '');
    setLocalSolturaCoordenadas(currentOutcome?.local_soltura_coordenadas || '');
    setOrgaoAutorizador(currentOutcome?.orgao_autorizador || '');
    setInstituicaoDestino(currentOutcome?.instituicao_destino || '');
    setResponsavelDestino(currentOutcome?.responsavel_destino || '');
    setObservacoesClinicasEnviadas(currentOutcome?.observacoes_clinicas_enviadas || '');
    setCausaPresumida(currentOutcome?.causa_presumida || '');
    setNecropsiaRealizada(Boolean(currentOutcome?.necropsia_realizada));
    setObservacoesObito(currentOutcome?.observacoes_obito || '');
    setOutroTipoDesfecho(outroDetails.tipo);
    setJustificativaOutro(outroDetails.justificativa);
  };

  const validateForm = () => {
    if (!outcomeType) {
      toast.error('Selecione o tipo de desfecho');
      return false;
    }
    if (!justificativaClinica || justificativaClinica.length < 10) {
      toast.error('Justificativa clínica é obrigatória (mínimo 10 caracteres)');
      return false;
    }
    
    // Validações específicas
    if (outcomeType === 'soltura' && !localSolturaCidade) {
      toast.error('Informe a cidade de soltura');
      return false;
    }
    if (outcomeType === 'transferencia' && !instituicaoDestino) {
      toast.error('Informe a instituição destino');
      return false;
    }
    if (outcomeType === 'outro' && !outroTipoDesfecho.trim()) {
      toast.error('Informe o nome do outro tipo de desfecho');
      return false;
    }
    if (outcomeType === 'outro' && !justificativaOutro) {
      toast.error('Justificativa é obrigatória para desfecho tipo "Outro"');
      return false;
    }
    
    return true;
  };

  const handlePrepareSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmDialog(true);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        outcome_type: outcomeType,
        outcome_date: outcomeDate || null,
        outcome_time: outcomeTime || null,
        justificativa_clinica: justificativaClinica,
        observacoes_administrativas: observacoesAdmin || null,
        
        // Alta Clínica
        condicao_clinica_final: condicaoClinicaFinal || null,
        recomendacoes_pos_alta: recomendacoesPosAlta || null,
        responsavel_retirada_nome: responsavelRetiradaNome || null,
        responsavel_retirada_documento: responsavelRetiradaDoc || null,
        
        // Soltura
        local_soltura_cidade: localSolturaCidade || null,
        local_soltura_estado: localSolturaEstado || null,
        local_soltura_coordenadas: localSolturaCoordenadas || null,
        orgao_autorizador: orgaoAutorizador || null,
        
        // Transferência
        instituicao_destino: instituicaoDestino || null,
        responsavel_destino: responsavelDestino || null,
        observacoes_clinicas_enviadas: observacoesClinicasEnviadas || null,
        
        // Óbito/Eutanásia
        causa_presumida: causaPresumida || null,
        necropsia_realizada: necropsiaRealizada,
        observacoes_obito: observacoesObito || null,
        
        // Outro
        justificativa_outro: outcomeType === 'outro'
          ? `Tipo informado: ${outroTipoDesfecho.trim()} | ${justificativaOutro || ''}`.trim()
          : (justificativaOutro || null)
      };
      
      if (outcome?.id) {
        await axios.put(`${API}/animals/${animalId}/outcome`, payload);
      } else {
        await axios.post(`${API}/animals/${animalId}/outcome`, payload);
      }
      
      toast.success(outcome?.id ? 'Desfecho atualizado com sucesso!' : 'Desfecho registrado com sucesso!');
      setShowForm(false);
      setShowConfirmDialog(false);
      resetForm();
      fetchOutcome();
      
      if (onOutcomeChange) {
        onOutcomeChange();
      }
    } catch (error) {
      console.error('Error saving outcome:', error);
      toast.error(getApiErrorMessage(error, outcome?.id ? 'Erro ao atualizar desfecho' : 'Erro ao registrar desfecho'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditOutcome = () => {
    populateFormFromOutcome(outcome);
    setShowForm(true);
  };

  const handleRevert = async () => {
    try {
      await axios.delete(`${API}/animals/${animalId}/outcome`);
      toast.success('Desfecho excluído. Animal retorna para tratamento.');
      setShowRevertDialog(false);
      setOutcome(null);
      
      if (onOutcomeChange) {
        onOutcomeChange();
      }
    } catch (error) {
      console.error('Error reverting outcome:', error);
      toast.error(getApiErrorMessage(error, 'Erro ao excluir desfecho'));
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/animals/${animalId}/outcome/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `desfecho_${animal?.ibama_id || animalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao gerar PDF'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleUploadDocument = async (file) => {
    if (!file) return;
    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      formData.append('document_name', documentName || file.name);
      await axios.post(`${API}/animals/${animalId}/outcome/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento anexado ao desfecho');
      setDocumentName('');
      fetchOutcome();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao anexar documento'));
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!attachment?.file_url) return;
    if (!window.confirm('Excluir este anexo do desfecho?')) return;
    try {
      await axios.delete(`${API}/animals/${animalId}/outcome/attachments`, {
        params: { file_url: attachment.file_url }
      });
      toast.success('Anexo excluído');
      fetchOutcome();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erro ao excluir anexo'));
    }
  };

  const canRegisterOutcome = hasPermission(user, 'animals.edit_clinical');
  const canEditRegisteredOutcome = ['administrador', 'programador'].includes(user?.role);
  const canUseOutcomeForm = outcome ? canEditRegisteredOutcome : canRegisterOutcome;
  const canRevert = hasPermission(user, 'records.delete');
  const canDeleteAttachments = ['administrador', 'programador'].includes(user?.role);

  const getOutcomeConfig = (type) => {
    return OUTCOME_TYPES.find(t => t.value === type) || OUTCOME_TYPES[6];
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  // Se já tem desfecho registrado
  if (outcome && !showForm) {
    const config = getOutcomeConfig(outcome.outcome_type);
    const Icon = config.icon;
    
    return (
      <div className="space-y-6" data-testid="outcome-registered">
        {/* Banner de Status */}
        <Card className={`border-2 ${config.color}`}>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${config.color.replace('text-', 'bg-').split(' ')[0]}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <CardTitle className="text-xl">Desfecho Registrado</CardTitle>
                  <Badge className={config.color}>{config.label}</Badge>
                </div>
              </div>
              <div className="flex gap-2">
                {canEditRegisteredOutcome && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditOutcome}
                    className="border-gray-300"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar Desfecho
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="border-gray-300"
                >
                  {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  PDF
                </Button>
                {canRevert && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRevertDialog(true)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Undo2 className="w-4 h-4 mr-1" />
                    Excluir Desfecho
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Dados gerais */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-2">
                <Calendar className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Data</p>
                  <p className="font-medium">
                    {formatOutcomeDate(outcome.outcome_date)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Hora</p>
                  <p className="font-medium">{outcome.outcome_time || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <User className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Responsável Técnico</p>
                  <p className="font-medium">{outcome.responsavel_tecnico_nome || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Justificativa */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-1">Justificativa Clínica</p>
              <p className="p-3 bg-white rounded-lg border">{outcome.justificativa_clinica}</p>
            </div>

            {/* Campos específicos por tipo */}
            {outcome.outcome_type === 'alta_clinica' && (
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700">Dados da Alta Clínica</p>
                {outcome.condicao_clinica_final && (
                  <div><span className="text-gray-500">Condição Final:</span> {outcome.condicao_clinica_final}</div>
                )}
                {outcome.responsavel_retirada_nome && (
                  <div><span className="text-gray-500">Responsável Retirada:</span> {outcome.responsavel_retirada_nome}</div>
                )}
                {outcome.recomendacoes_pos_alta && (
                  <div><span className="text-gray-500">Recomendações:</span> {outcome.recomendacoes_pos_alta}</div>
                )}
              </div>
            )}

            {outcome.outcome_type === 'soltura' && (
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  Dados da Soltura
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {outcome.local_soltura_cidade && (
                    <div><span className="text-gray-500">Local:</span> {outcome.local_soltura_cidade} - {outcome.local_soltura_estado}</div>
                  )}
                  {outcome.local_soltura_coordenadas && (
                    <div><span className="text-gray-500">Coordenadas:</span> {outcome.local_soltura_coordenadas}</div>
                  )}
                  {outcome.orgao_autorizador && (
                    <div><span className="text-gray-500">Órgão Autorizador:</span> {outcome.orgao_autorizador}</div>
                  )}
                </div>
              </div>
            )}

            {outcome.outcome_type === 'transferencia' && (
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  Dados da Transferência
                </p>
                {outcome.instituicao_destino && (
                  <div><span className="text-gray-500">Instituição Destino:</span> {outcome.instituicao_destino}</div>
                )}
                {outcome.responsavel_destino && (
                  <div><span className="text-gray-500">Responsável Destino:</span> {outcome.responsavel_destino}</div>
                )}
                {outcome.observacoes_clinicas_enviadas && (
                  <div><span className="text-gray-500">Observações Clínicas:</span> {outcome.observacoes_clinicas_enviadas}</div>
                )}
              </div>
            )}

            {(outcome.outcome_type === 'obito' || outcome.outcome_type === 'eutanasia') && (
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700">Dados do Óbito</p>
                {outcome.causa_presumida && (
                  <div><span className="text-gray-500">Causa Presumida:</span> {outcome.causa_presumida}</div>
                )}
                <div>
                  <span className="text-gray-500">Necropsia:</span>{' '}
                  <Badge variant="outline">{outcome.necropsia_realizada ? 'Realizada' : 'Não realizada'}</Badge>
                </div>
                {outcome.observacoes_obito && (
                  <div><span className="text-gray-500">Observações:</span> {outcome.observacoes_obito}</div>
                )}
              </div>
            )}

            {outcome.outcome_type === 'outro' && outcome.justificativa_outro && (
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-1">Detalhes do tipo "Outro"</p>
                <p className="text-sm text-gray-700">{outcome.justificativa_outro}</p>
              </div>
            )}

            {outcome.observacoes_administrativas && (
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-1">Observações Administrativas</p>
                <p className="text-sm text-gray-600">{outcome.observacoes_administrativas}</p>
              </div>
            )}

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Documentos do Desfecho</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="autorizacao">Autorização</SelectItem>
                      <SelectItem value="transferencia">Transferência / GTA</SelectItem>
                      <SelectItem value="laudo">Laudo / Atestado</SelectItem>
                      <SelectItem value="midia">Imagem / Vídeo</SelectItem>
                      <SelectItem value="outro_documento">Outro documento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Nome do documento</Label>
                  <Input value={documentName} onChange={(e) => setDocumentName(e.target.value)} placeholder="Ex: GTA 123, atestado sanitário" />
                </div>
              </div>
              <CameraFileInput
                accept={documentType === 'midia' ? 'image/*,video/*' : '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx'}
                disabled={uploadingDocument}
                onFileSelected={handleUploadDocument}
                fileLabel={uploadingDocument ? 'Enviando...' : 'Selecionar arquivo'}
                cameraLabel="Tirar foto"
              />
              <p className="text-xs text-gray-500">Selecione "Imagem / Vídeo" para anexar fotos do desfecho.</p>
              {outcome.attachments?.length > 0 && (
                <div className="space-y-2">
                  {outcome.attachments.map((attachment, index) => (
                    <div key={`${attachment.file_url}-${index}`} className="flex items-start justify-between gap-2 rounded border bg-white p-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{attachment.document_name || attachment.filename}</p>
                        <a
                          className="text-blue-600 underline"
                          href={`${API}${attachment.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => handleOpenAttachment(event, attachment)}
                        >
                          Abrir arquivo
                        </a>
                      </div>
                      {canDeleteAttachments && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-red-600"
                          onClick={() => handleDeleteAttachment(attachment)}
                          title="Excluir anexo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dialog de Exclusão */}
        <Dialog open={showRevertDialog} onOpenChange={setShowRevertDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Excluir Desfecho
              </DialogTitle>
              <DialogDescription>
                Esta ação irá excluir o desfecho e retornar o animal para o status "Em tratamento". 
                <strong> Esta ação deve ser usada apenas em casos excepcionais.</strong>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowRevertDialog(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleRevert}>
                Confirmar Exclusão
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Se não tem desfecho - mostrar formulário
  return (
    <div className="space-y-6" data-testid="outcome-tab">
      {/* Header */}
      <Card className="border-emerald-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="text-emerald-800 flex items-center gap-2">
                <FileCheck className="w-5 h-5" />
                {outcome ? 'Editar Desfecho do Animal' : 'Desfecho do Animal'}
              </CardTitle>
              <CardDescription>
                {outcome
                  ? 'Atualize os campos do desfecho já cadastrado.'
                  : 'Registre o desfecho final do animal (alta, soltura, transferência, etc.)'}
              </CardDescription>
            </div>
            {!outcome && canRegisterOutcome && (
              <Button
                onClick={() => setShowForm(true)}
                className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]"
                data-testid="register-outcome-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Registrar Desfecho
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!canUseOutcomeForm ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>{outcome ? 'Apenas administradores e programador podem editar desfechos cadastrados.' : 'Apenas veterinários e coordenação podem registrar desfechos.'}</p>
            </div>
          ) : !showForm ? (
            <div className="text-center py-8">
              <FileCheck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">Nenhum desfecho registrado para este animal</p>
              <p className="text-sm text-gray-400">
                Clique em "Registrar Desfecho" para finalizar o acompanhamento
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Formulário de Registro */}
      {showForm && canUseOutcomeForm && (
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">{outcome ? 'Editar Desfecho' : 'Formulário de Desfecho'}</CardTitle>
            <CardDescription>Preencha todos os campos obrigatórios (*)</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePrepareSubmit} className="space-y-6">
              {/* Tipo de Desfecho */}
              <div>
                <Label className="text-base font-semibold">Tipo de Desfecho *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                  {OUTCOME_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setOutcomeType(type.value)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          outcomeType === type.value
                            ? `${type.color} ring-2 ring-offset-2 ring-emerald-400`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`outcome-type-${type.value}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5" />
                          <div>
                            <p className="font-medium">{type.label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Data e Hora */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Data do Desfecho</Label>
                  <Input
                    type="date"
                    value={outcomeDate}
                    onChange={(e) => setOutcomeDate(e.target.value)}
                    data-testid="outcome-date"
                  />
                </div>
                <div>
                  <Label>Hora</Label>
                  <Input
                    type="time"
                    value={outcomeTime}
                    onChange={(e) => setOutcomeTime(e.target.value)}
                    data-testid="outcome-time"
                  />
                </div>
              </div>

              {/* Justificativa Clínica - Obrigatória */}
              <div>
                <Label>Justificativa Clínica *</Label>
                <Textarea
                  value={justificativaClinica}
                  onChange={(e) => setJustificativaClinica(e.target.value)}
                  placeholder="Descreva a justificativa clínica para este desfecho..."
                  rows={4}
                  required
                  data-testid="justificativa-clinica"
                />
              </div>

              {/* Campos específicos por tipo */}
              {outcomeType === 'alta_clinica' && (
                <Card className="bg-green-50 border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-green-800 text-lg flex items-center gap-2">
                      <Heart className="w-5 h-5" />
                      Dados da Alta Clínica
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Condição Clínica Final</Label>
                      <Textarea
                        value={condicaoClinicaFinal}
                        onChange={(e) => setCondicaoClinicaFinal(e.target.value)}
                        placeholder="Descreva a condição clínica final do animal..."
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Recomendações Pós-Alta</Label>
                      <Textarea
                        value={recomendacoesPosAlta}
                        onChange={(e) => setRecomendacoesPosAlta(e.target.value)}
                        placeholder="Recomendações para o responsável..."
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Nome do Responsável pela Retirada</Label>
                        <Input
                          value={responsavelRetiradaNome}
                          onChange={(e) => setResponsavelRetiradaNome(e.target.value)}
                          placeholder="Nome completo"
                        />
                      </div>
                      <div>
                        <Label>Documento (opcional)</Label>
                        <Input
                          value={responsavelRetiradaDoc}
                          onChange={(e) => setResponsavelRetiradaDoc(e.target.value)}
                          placeholder="CPF ou RG"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {outcomeType === 'soltura' && (
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-emerald-800 text-lg flex items-center gap-2">
                      <Leaf className="w-5 h-5" />
                      Dados da Soltura
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Cidade de Soltura *</Label>
                        <Input
                          value={localSolturaCidade}
                          onChange={(e) => setLocalSolturaCidade(e.target.value)}
                          placeholder="Nome da cidade"
                          required
                        />
                      </div>
                      <div>
                        <Label>Estado</Label>
                        <Select value={localSolturaEstado} onValueChange={setLocalSolturaEstado}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            {ESTADOS_BR.map(uf => (
                              <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Coordenadas GPS (opcional)</Label>
                      <Input
                        value={localSolturaCoordenadas}
                        onChange={(e) => setLocalSolturaCoordenadas(e.target.value)}
                        placeholder="Ex: -23.5505, -46.6333"
                      />
                    </div>
                    <div>
                      <Label>Órgão Autorizador</Label>
                      <Input
                        value={orgaoAutorizador}
                        onChange={(e) => setOrgaoAutorizador(e.target.value)}
                        placeholder="Ex: IBAMA, ICMBio"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {outcomeType === 'transferencia' && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-blue-800 text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Dados da Transferência
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Instituição Destino *</Label>
                      <Input
                        value={instituicaoDestino}
                        onChange={(e) => setInstituicaoDestino(e.target.value)}
                        placeholder="Nome da instituição"
                        required
                      />
                    </div>
                    <div>
                      <Label>Responsável no Destino</Label>
                      <Input
                        value={responsavelDestino}
                        onChange={(e) => setResponsavelDestino(e.target.value)}
                        placeholder="Nome do responsável"
                      />
                    </div>
                    <div>
                      <Label>Observações Clínicas Enviadas</Label>
                      <Textarea
                        value={observacoesClinicasEnviadas}
                        onChange={(e) => setObservacoesClinicasEnviadas(e.target.value)}
                        placeholder="Informações clínicas enviadas junto com o animal..."
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {(outcomeType === 'obito' || outcomeType === 'eutanasia') && (
                <Card className={`${outcomeType === 'eutanasia' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className={`text-lg flex items-center gap-2 ${outcomeType === 'eutanasia' ? 'text-red-800' : 'text-gray-800'}`}>
                      {outcomeType === 'eutanasia' ? <Syringe className="w-5 h-5" /> : <Skull className="w-5 h-5" />}
                      Dados do {outcomeType === 'eutanasia' ? 'Procedimento' : 'Óbito'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Causa Presumida</Label>
                      <Textarea
                        value={causaPresumida}
                        onChange={(e) => setCausaPresumida(e.target.value)}
                        placeholder="Descreva a causa presumida..."
                        rows={2}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="necropsia"
                        checked={necropsiaRealizada}
                        onChange={(e) => setNecropsiaRealizada(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <Label htmlFor="necropsia" className="cursor-pointer">Necropsia Realizada</Label>
                    </div>
                    <div>
                      <Label>Observações</Label>
                      <Textarea
                        value={observacoesObito}
                        onChange={(e) => setObservacoesObito(e.target.value)}
                        placeholder="Observações adicionais..."
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {outcomeType === 'outro' && (
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-yellow-800 text-lg flex items-center gap-2">
                      <HelpCircle className="w-5 h-5" />
                      Justificativa Adicional
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <Label>Nome do Outro Tipo de Desfecho *</Label>
                      <Input
                        value={outroTipoDesfecho}
                        onChange={(e) => setOutroTipoDesfecho(e.target.value)}
                        placeholder="Ex: Encaminhamento para pesquisa"
                        required
                      />
                    </div>
                    <div>
                      <Label>Justificativa para "Outro" *</Label>
                      <Textarea
                        value={justificativaOutro}
                        onChange={(e) => setJustificativaOutro(e.target.value)}
                        placeholder="Explique detalhadamente o motivo deste desfecho..."
                        rows={4}
                        required
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Observações Administrativas */}
              <div>
                <Label>Observações Administrativas (opcional)</Label>
                <Textarea
                  value={observacoesAdmin}
                  onChange={(e) => setObservacoesAdmin(e.target.value)}
                  placeholder="Observações administrativas..."
                  rows={2}
                />
              </div>

              {/* Botões */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="min-h-[44px]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]"
                  data-testid="submit-outcome-btn"
                >
                  {outcome ? 'Revisar Alterações' : 'Revisar e Confirmar'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Confirmação */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-emerald-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              {outcome ? 'Confirmar Alteração de Desfecho' : 'Confirmar Registro de Desfecho'}
            </DialogTitle>
            <DialogDescription>
              {outcome
                ? 'Revise os dados antes de confirmar a alteração do desfecho cadastrado.'
                : <>Revise os dados antes de confirmar. <strong>Esta ação irá marcar o animal como INATIVO</strong> e removerá das listas operacionais.</>}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm"><strong>Tipo:</strong> {getOutcomeConfig(outcomeType).label}</p>
              <p className="text-sm"><strong>Justificativa:</strong> {justificativaClinica.substring(0, 100)}...</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={saving}>
              Voltar e Editar
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={saving}
              data-testid="confirm-outcome-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {outcome ? 'Confirmar Alteração' : 'Confirmar Desfecho'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
